import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// Premium shimmer overlay — brand-aware (primary tint instead of plain white)
const Shimmer = ({ className }: { className?: string }) => (
  <div className={cn('relative overflow-hidden bg-muted/70', className)}>
    <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.8s_infinite] bg-gradient-to-r from-transparent via-primary/10 to-transparent" />
  </div>
);

export const CarCardSkeleton = () => (
  <Card className="overflow-hidden relative border-border/60 shadow-xs">
    {/* Image area with aspect ratio + corner badge placeholders */}
    <div className="relative w-full aspect-[4/3] overflow-hidden bg-muted">
      <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.8s_infinite] bg-gradient-to-r from-transparent via-primary/10 to-transparent" />
      {/* Floating badge placeholder */}
      <Shimmer className="absolute top-3 left-3 h-5 w-16 rounded-full" />
      {/* Photo counter placeholder */}
      <Shimmer className="absolute bottom-3 right-3 h-6 w-12 rounded-full" />
    </div>

    <div className="p-4 space-y-3">
      {/* Title */}
      <Shimmer className="h-5 w-4/5 rounded-md" />
      {/* Price — larger */}
      <Shimmer className="h-7 w-1/2 rounded-md bg-primary/10" />
      {/* Spec row with icons */}
      <div className="flex gap-2 pt-1">
        <Shimmer className="h-4 w-16 rounded-sm" />
        <Shimmer className="h-4 w-12 rounded-sm" />
        <Shimmer className="h-4 w-14 rounded-sm" />
      </div>
      {/* Location */}
      <Shimmer className="h-3 w-2/3 rounded-sm" />
      {/* Divider */}
      <div className="h-px bg-border/60" />
      {/* CTA button */}
      <Shimmer className="h-9 w-full rounded-md bg-primary/10" />
    </div>
  </Card>
);

export const CarGridSkeleton = ({ count = 8 }: { count?: number }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
    {Array.from({ length: count }).map((_, i) => (
      <CarCardSkeleton key={i} />
    ))}
  </div>
);

export const ListingDetailSkeleton = () => (
  <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
    {/* Image gallery skeleton */}
    <Card className="p-4">
      <Skeleton className="w-full h-96 rounded-lg mb-4" />
      <div className="flex gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-20 rounded-sm" />
        ))}
      </div>
    </Card>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main content */}
      <div className="lg:col-span-2 space-y-4">
        <Card className="p-6 space-y-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-10 w-1/3" />
          <div className="flex gap-4">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-6 w-24" />
          </div>
          <Skeleton className="h-32 w-full" />
        </Card>
      </div>

      {/* Sidebar */}
      <div className="space-y-4">
        <Card className="p-6 space-y-3">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </Card>
      </div>
    </div>
  </div>
);

export const DashboardStatsSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    {Array.from({ length: 4 }).map((_, i) => (
      <Card key={i} className="p-6 relative overflow-hidden">
        <div className="flex items-center justify-between mb-2">
          <div className="relative h-4 w-24 overflow-hidden rounded-sm bg-muted">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>
          <div className="relative h-8 w-8 overflow-hidden rounded-full bg-muted">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>
        </div>
        <div className="relative h-8 w-20 mb-2 overflow-hidden rounded-sm bg-muted">
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>
        <div className="relative h-3 w-32 overflow-hidden rounded-sm bg-muted">
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>
      </Card>
    ))}
  </div>
);
